"use client";

import React, { useEffect, useRef } from "react";
import ListingCard from "@/src/shared/components/listing/ListingCard";
import ListingCardSkeleton from "@/src/shared/components/listing/skeletons/ListingCardSkeleton";
import type { ICampaignListing } from "@/src/shared/types/listing";

const GRID =
  "grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";

interface IListingsGridProps {
  listings: ICampaignListing[];
  /** Every card is paid-boosted: each gets the "Sponsored" pill. */
  sponsored?: boolean;
  cardQuery?: (item: ICampaignListing) => Record<string, string> | undefined;
  isLoading?: boolean;
  /** More pages exist: load them as the end comes into view. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  /** The scroller to watch (the drawer's body); the window when absent. */
  scrollRoot?: HTMLElement | null;
}

/** Cards in a grid (a rail's page, its drawer, a view-all panel), paging in as you scroll. */
const ListingsGrid: React.FC<IListingsGridProps> = ({
  listings,
  sponsored = false,
  cardQuery,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  scrollRoot,
}) => {
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || !onLoadMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isLoadingMore) onLoadMore();
      },
      { root: scrollRoot ?? null, rootMargin: "400px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore, scrollRoot]);

  if (isLoading) {
    return (
      <div className={GRID} aria-busy>
        {Array.from({ length: 10 }).map((_, index) => (
          <ListingCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <p className="py-16 text-center text-ink-gray">
        No stays to show here right now. New ones are added often.
      </p>
    );
  }

  return (
    <>
      <div className={GRID}>
        {listings.map((item, index) => (
          <ListingCard
            key={`${item.listing.id}-${index}`}
            data={item.listing}
            linkQuery={cardQuery?.(item)}
            isSponsored={sponsored}
          />
        ))}
        {isLoadingMore &&
          Array.from({ length: 4 }).map((_, index) => (
            <ListingCardSkeleton key={`more-${index}`} />
          ))}
      </div>
      {hasMore && <div ref={sentinel} aria-hidden className="h-px" />}
    </>
  );
};

export default ListingsGrid;
